import {
  SimpleForm,
  TextInput,
  required,
  NumberInput,
  SelectInput,
  Toolbar,
  SaveButton,
  BooleanInput,
  TextField,
  Datagrid,
  useList,
  ListContextProvider,
  ResourceContextProvider,
  useRecordContext,
} from "react-admin";
import { productTypeOptions, statesList } from "../../../lib/constants";
import { Grid, Typography } from "@mui/material";
import StateCheckboxArrayInput from "../StateCheckboxArrayInput";

// Human description of one agency's customization of this carrier.
function describeCustomization(row) {
  const parts = [];
  if (row.blocked) parts.push("Blocked");
  if (row.disabled) parts.push("Turned off for agents");
  if (row.overrideStates) {
    parts.push(
      row.overrideStates.length
        ? `Available only in: ${row.overrideStates.join(", ")}`
        : "Available in no states"
    );
  }
  return parts.join("; ");
}

// #5 — every agency offers a carrier by default, so this lists only the agencies
// that changed it, with WHAT they changed (the Status column). Data comes
// embedded on the carrier record (carrierCustomizations) from GET
// /api/carrier/:id. Clicking a row opens that agency.
const CarrierCustomizationsPanel = () => {
  const record = useRecordContext();
  const rows = (record?.carrierCustomizations || []).map((r) => ({
    id: r.agencyId,
    agencyName: r.agencyName,
    status: describeCustomization(r),
  }));
  const listContext = useList({ data: rows });
  return (
    <>
      <Typography variant="h6">Agencies with custom settings</Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 1 }}>
        Every agency offers this carrier by default. Listed here are the agencies
        that changed it. Click a row to open the agency.
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No agencies have customized this carrier.
        </Typography>
      ) : (
        <ResourceContextProvider value="agency">
          <ListContextProvider value={listContext}>
            <Datagrid
              bulkActionButtons={false}
              rowClick={(id) => `/agency/${id}`}
            >
              <TextField source="agencyName" label="Agency" sortable={false} />
              <TextField source="status" label="Status" sortable={false} />
            </Datagrid>
          </ListContextProvider>
        </ResourceContextProvider>
      )}
    </>
  );
};

const ActionToolbar = ({ readonly }) => {
  if (readonly) {
    return null;
  }
  return (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );
};

const CarrierFields = ({ user, isAgencyAdmin, isFullAdmin }) => (
  <SimpleForm warnWhenUnsavedChanges toolbar={<ActionToolbar readonly={!isFullAdmin(user)} />}>
    <Grid container spacing={4}>
      <Grid item xs={6}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Carrier Information</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextInput
              source="value"
              label="Name"
              fullWidth
              disabled={!isFullAdmin(user)}
            />
          </Grid>
          <Grid item xs={6}>
            <NumberInput
              source="carrierId"
              label="Carrier ID"
              fullWidth
              disabled={!isFullAdmin(user)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              source="tier"
              label="Tier"
              fullWidth
              disabled={!isFullAdmin(user)}
            />
          </Grid>
          <Grid item xs={12}>
            {/* #91 - agents pick carriers type-first, so a carrier without a
                type would never be offered; the type is mandatory. */}
            <SelectInput
              source="productType"
              label="Type"
              choices={productTypeOptions}
              validate={required()}
              disabled={!isFullAdmin(user)}
            />
          </Grid>
          <Grid item xs={12}>
            <StateCheckboxArrayInput
              label="Available states"
              source="availableStates"
              fullWidth
              choices={statesList}
              disabled={!isFullAdmin(user)}
            />
            <br />
            <Typography variant="caption">
              States where this carrier is available to agents. Agents only see
              this carrier if they're licensed in one of these states. Leave
              blank to make it available everywhere. An agency can override this
              list for its own agents.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <StateCheckboxArrayInput
              label="States requiring a separate contract"
              source="states"
              fullWidth
              choices={statesList}
              disabled={!isFullAdmin(user)}
            />
            <br />
            <Typography variant="caption">
              Carrier requires separate contracts for these states. If the
              submission process is identical for all states in which a carrier
              is active, leave this field blank. (Unrelated to availability
              above.)
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <BooleanInput
              source="isInactive"
              label="Active"
              format={(v) => !v}
              parse={(v) => !v}
              disabled={!isFullAdmin(user)}
            />
            <Typography variant="caption">
              Inactive carriers will not appear in the carrier selection
              dropdown on the contract request form.
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {isFullAdmin(user) && (
        <>
          <Grid item xs={6}>
            <CarrierCustomizationsPanel />
          </Grid>
        </>
      )}
    </Grid>
  </SimpleForm>
);

export default CarrierFields;
